import { FC, SVGProps, memo, useMemo } from 'react';

import classnames from 'classnames';
import * as Flags from 'country-flag-icons/react/3x2';

import { getCountryCodeByName, getCountryNameByCode } from 'utils/countries';

import { flagCodes } from 'constants/flagCodes';

import styles from './CountryPreview.module.scss';

type FlagComponent = FC<SVGProps<SVGSVGElement>>;
type FlagsType = Record<string, FlagComponent>;

interface CountryPreviewProps {
  className?: string;
  countryCode?: string | null;
  countryName?: string | null;
  isCountryCodeLabel?: boolean;
  iconOnly?: boolean;
}

const CountryPreview: FC<CountryPreviewProps> = ({
  className = '',
  countryCode,
  countryName = '',
  isCountryCodeLabel = false,
  iconOnly = false,
  ...rest
}) => {
  const displayedCountryCode = useMemo(() => {
    return (
      countryCode?.toUpperCase() ??
      (countryName && getCountryCodeByName(countryName)) ??
      ''
    );
  }, [countryCode, countryName]);

  const displayedCountryName = useMemo(() => {
    return (
      countryName ||
      (displayedCountryCode ? getCountryNameByCode(displayedCountryCode) : '')
    );
  }, [countryName, displayedCountryCode]);

  const FlagIcon = displayedCountryCode
    ? (Flags as FlagsType)[displayedCountryCode]
    : null;

  const icon = useMemo(() => {
    if (
      !displayedCountryCode ||
      !flagCodes.includes(displayedCountryCode) ||
      !FlagIcon
    ) {
      return null;
    }
    return (
      <FlagIcon
        className={styles.imgIcon}
        {...(rest as SVGProps<SVGSVGElement>)}
      />
    );
  }, [displayedCountryCode, FlagIcon, rest]);

  if (!displayedCountryCode) return null;
  if (iconOnly) return icon;

  const text = isCountryCodeLabel ? displayedCountryCode : displayedCountryName;

  return (
    <div
      data-qa="countyPreview"
      data-qa-value={displayedCountryCode}
      className={classnames(styles.countryPreview, {
        [className]: !!className
      })}
      title={displayedCountryName || undefined}
    >
      <div className={styles.iconWrapper}>{icon}</div>
      <div className={styles.textWrapper}>{text}</div>
    </div>
  );
};

export default memo(CountryPreview);
